import { useCallback, useMemo } from 'react';
import moment from 'moment';
import locale from '@glu/locale';
import { DatePicker } from '@glu/datepicker-react';
import { DateCallback } from '@glu/datepicker-react/types/DatePicker';
import { IDateFilterValues, IFilterable, IFilterProps } from '../Filters/Filters';

export const DISPLAY_DATE_FORMAT = 'YYYY-MM-DD';

export interface IDateRangeFilterProps extends IFilterProps {
  /** The current value of the filter. Since state is not managed
   * locally this is required to display any current filter values.
   */
  value?: IDateFilterValues;
}

export const defaultParseDateFilterValue = ({
  event,
  filterable,
}: {
  event: {
    dateFormat: string;
    name: string;
    datesPicked: DateCallback;
  };
  filterable: IFilterable;
}) => {
  const { dateFormat, datesPicked } = event;
  return {
    filterable,
    value: {
      // These formats are the expected formats from ag-grid
      // and the defaultParseDateFilterValue is designed to be used with ag-grid.
      // If another format is passed the filter will not be applied.
      dateFrom: datesPicked[0]!.format('YYYY-MM-DD'),
      dateTo: datesPicked[1]!.format('YYYY-MM-DD'),
      type: 'inRange',
      format: dateFormat,
    },
  };
};

const DateRangeFilter = ({ filterable, onChange, value }: IDateRangeFilterProps) => {
  const { field, filterOptions, headerName } = filterable;
  const dateFormat = filterOptions?.componentProps?.format || DISPLAY_DATE_FORMAT;
  const { dateFrom, dateTo } = value || {};

  const filterValue = useMemo(
    () =>
      value
        ? [moment(dateFrom).format(dateFormat), moment(dateTo || dateFrom).format(dateFormat)]
        : undefined,
    [dateFormat, dateFrom, dateTo, value]
  );

  const onDatePicked = useCallback(
    (name: string, datesPicked: DateCallback) => {
      const changeValue = (filterOptions?.parseFilterValue || defaultParseDateFilterValue)({
        event: {
          dateFormat,
          datesPicked,
          name,
        },
        filterable,
      });
      onChange(changeValue);
    },
    [dateFormat, filterOptions?.parseFilterValue, filterable, onChange]
  );

  return (
    <DatePicker
      disableInputAPI
      forcePositionY
      labelText={`${headerName} ${locale.get('dataComponents.datepicker')}`}
      name={field}
      onApply={onDatePicked}
      screenReaderLabel
      value={filterValue}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...filterOptions?.componentProps}
    />
  );
};

export default DateRangeFilter;
